import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faCheck, faMinus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react'

import { FOCUS_RING } from './focusRing.ts'

export interface CheckboxProps extends Omit<ComponentPropsWithRef<'input'>, 'type'> {
  label?: ReactNode
  leadingIcon?: IconDefinition
  indeterminate?: boolean
  containerClassName?: string
  /** When true, disables interaction and hides the checkbox from assistive technology. */
  visualOnly?: boolean
}

function joinClasses(...classes: Array<string | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

/**
 * Reusable Quave Run checkbox matching Figma ingredient set 4857:1396.
 */
export function Checkbox({
  label,
  leadingIcon,
  indeterminate = false,
  containerClassName,
  visualOnly = false,
  className,
  id,
  disabled = false,
  tabIndex,
  ref,
  ...rest
}: CheckboxProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const inputRef = useRef<HTMLInputElement>(null)

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node

      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref],
  )

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const isDisabled = disabled || visualOnly
  const status = indeterminate ? 'partial' : rest.checked ? 'selected' : 'blank'

  return (
    <label
      aria-hidden={visualOnly || undefined}
      data-status={status}
      data-disabled={isDisabled || undefined}
      htmlFor={inputId}
      className={joinClasses(
        'inline-flex w-fit items-start gap-2',
        isDisabled ? 'cursor-default' : 'cursor-pointer',
        visualOnly ? 'pointer-events-none' : undefined,
        containerClassName,
      )}
    >
      <span className="relative mt-0.5 size-[16px] shrink-0">
        <input
          {...rest}
          ref={setInputRef}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          tabIndex={visualOnly ? -1 : tabIndex}
          aria-checked={indeterminate ? 'mixed' : undefined}
          aria-hidden={visualOnly || undefined}
          className={joinClasses(
            'peer absolute inset-0 size-[16px] appearance-none rounded-[4px] border',
            FOCUS_RING,
            indeterminate
              ? disabled
                ? 'border-stroke-disabled bg-fill-disabled'
                : 'border-stroke-selected bg-fill-selected'
              : 'border-stroke-strong bg-fill-white checked:border-stroke-selected checked:bg-fill-selected disabled:border-stroke-disabled disabled:bg-fill-white disabled:checked:border-stroke-disabled disabled:checked:bg-fill-disabled',
            isDisabled ? 'cursor-default' : 'cursor-pointer',
            visualOnly ? 'pointer-events-none' : undefined,
            className,
          )}
        />

        {indeterminate ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-icon-inverse-strong"
          >
            <FontAwesomeIcon icon={faMinus} className="size-[10px]!" />
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 hidden items-center justify-center text-icon-inverse-strong peer-checked:flex"
          >
            <FontAwesomeIcon icon={faCheck} className="size-[10px]!" />
          </span>
        )}
      </span>

      {leadingIcon ? (
        <span
          aria-hidden="true"
          data-slot="checkbox-leading-icon"
          className={joinClasses(
            'inline-flex h-[20px] shrink-0 items-center justify-center',
            isDisabled ? 'text-icon-disabled' : 'text-icon-neutral',
          )}
        >
          <FontAwesomeIcon icon={leadingIcon} className="size-[16px]!" />
        </span>
      ) : null}

      {label ? (
        <span
          className={joinClasses('text-tP', isDisabled ? 'text-text-disabled' : 'text-text-strong')}
        >
          {label}
        </span>
      ) : null}
    </label>
  )
}
